/**
 * Reações da chamada: o balão de emojis que se escolhe e os emojis que sobem
 * pelo ecrã quando alguém reage. Sem back-end nenhum — viaja pelo mesmo canal
 * de dados do LiveKit que o quadro branco já usa (tópico "gv-data", campo `k`).
 *
 * Porque é que isto vale a pena: numa call com o micro mudo (a maioria do
 * tempo) não há forma barata de dizer "concordo", "boa" ou "estou aqui". Um
 * emoji que sobe 2 segundos resolve isso sem interromper quem fala.
 *
 * O overlay guarda `nameOf` numa ref para se subscrever ao motor UMA vez: se
 * dependesse da função (que o VoiceStage recria a cada render) re-subscrevia
 * sem parar e podia perder a reação que chegasse exactamente nesse instante.
 */

import { useEffect, useRef, useState } from "react";

import { voiceEngine } from "../lib/voice";

/** O conjunto fixo — chega para o essencial e não vira um teclado de emojis.
 *  Ordem pensada para o mais usado ficar primeiro. */
export const REACTIONS = ["👍", "❤️", "😂", "🎉", "😮", "👏", "🔥", "🙏"] as const;

/** Balão de escolha. Quem o abre/fecha é o VoiceStage (controla o botão e o
 *  fundo que fecha ao clicar fora); aqui só se desenham os emojis. */
export function ReactionPicker({ onPick }: { onPick: (emoji: string) => void }) {
  return (
    <div className="call-pop reaction-pick" role="menu" aria-label="Reagir">
      {REACTIONS.map((emoji) => (
        <button
          key={emoji}
          type="button"
          className="reaction-pick__btn"
          onClick={() => onPick(emoji)}
          title={`Reagir ${emoji}`}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

interface Floating {
  id: number;
  emoji: string;
  name: string;
  left: number;
}

/** Camada por cima dos tiles onde os emojis sobem e desaparecem. Não recebe
 *  cliques (`pointer-events:none` no CSS) — nunca tapa os controlos. */
export function ReactionsOverlay({ nameOf }: { nameOf: (identity: string) => string }) {
  const [items, setItems] = useState<Floating[]>([]);
  const seq = useRef(0);
  const nameRef = useRef(nameOf);
  nameRef.current = nameOf;

  useEffect(() => {
    const off = voiceEngine.subscribeReaction((from, emoji) => {
      if (!emoji) return;
      const id = (seq.current += 1);
      // Espalha na horizontal (8–92%) para dois emojis ao mesmo tempo não
      // subirem colados. Deriva do id (determinístico) — sem Math.random, que
      // o harness proíbe e que aqui não traria nada.
      const left = 8 + ((id * 37) % 85);
      setItems((prev) => [...prev, { id, emoji, name: nameRef.current(from), left }]);
      // O CSS anima ~2.4s; removemos um pouco depois para o nó sair já invisível.
      window.setTimeout(() => setItems((prev) => prev.filter((item) => item.id !== id)), 2600);
    });
    return off;
  }, []);

  if (!items.length) return null;

  return (
    <div className="reactions" aria-hidden="true">
      {items.map((item) => (
        <span key={item.id} className="reactions__item" style={{ left: `${item.left}%` }}>
          <span className="reactions__emoji">{item.emoji}</span>
          <span className="reactions__name">{item.name}</span>
        </span>
      ))}
    </div>
  );
}
